import React, { createRef, useState } from 'react';
import { Avatar, Box, Grid, IconButton, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { Clear as ClearIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { UploadArea, hovered } from '../../../../../Lab/Cases/AddResults/styled';
import { maxCasePdfReportSizeInMegabytes } from '../../../../../../utils/constants';
import { UploadItem } from '../../../../../../utils/types';

interface Props {
  files: UploadItem[];
  setFiles: React.Dispatch<React.SetStateAction<UploadItem[]>>;
}

const StepPdf = ({ files, setFiles }: Props): React.ReactElement => {
  const [isDragged, setIsDragged] = useState<boolean>(false);

  const inputRef = createRef<HTMLInputElement>();
  const inputSettings = {
    multiple: true,
    disabled: false,
    accept: {
      'application/pdf': ['.pdf'],
    },
  };

  const onDrop = (acceptedFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  };

  const { getInputProps, getRootProps } = useDropzone({
    ...inputSettings,
    onDrop,
    onDragLeave: () => setIsDragged(false),
    onDragOver: () => setIsDragged(true),
    noClick: false,
    maxSize: maxCasePdfReportSizeInMegabytes * 1024 ** 2,
  });

  const handleDelete = (index: number) => {
    if (files[index] instanceof File) {
      setFiles((prevFiles) => (prevFiles as File[]).filter((_, i) => i !== index));
    }
  };

  const downloadFile = (fileId: string) => {
    window.open(`${process.env.REACT_APP_AWS_PDF_REPORTS_URL}/${fileId}`, '_blank');
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h4">Document(s) upload</Typography>
      </Grid>
      <Grid item xs={12}>
        <UploadArea sx={(theme) => (isDragged ? { ...hovered(theme) } : {})} {...getRootProps()} mb={4}>
          <input {...getInputProps()} />
          <Box mb={1} width="100%" display="flex" justifyContent="center">
            <Avatar sx={(theme) => ({ bgcolor: `${theme.palette.primary.main}40`, width: 32, height: 32 })}>
              <UploadFileIcon fontSize="small" sx={(theme) => ({ fill: theme.palette.primary.main })} />
            </Avatar>
          </Box>
          <Box mb={1}>
            <Typography
              onClick={() => inputRef.current?.click()}
              variant="body2"
              component="span"
              sx={(theme) => ({ color: theme.palette.primary.main, borderBottom: `1px solid ${theme.palette.primary.main}` })}>
              Click to upload
            </Typography>{' '}
            <Typography variant="body2" component="span">
              or drag and drop PDF file
            </Typography>
          </Box>
          <Box>
            <Typography color={grey[600]} variant="body2">
              (max. {maxCasePdfReportSizeInMegabytes} MB)
            </Typography>
          </Box>
        </UploadArea>
      </Grid>
      <Grid item xs={12}>
        {files.map((file, i) => {
          const isFile = file instanceof File;

          return (
            <Box mb={2} display={'flex'}>
              <Box mr={2}>
                <Avatar sx={(theme) => ({ bgcolor: `${theme.palette.primary.main}40`, width: 32, height: 32 })}>
                  <UploadFileIcon fontSize="small" sx={(theme) => ({ fill: theme.palette.primary.main })} />
                </Avatar>
              </Box>
              <Box flexGrow={1}>
                <Typography sx={{ cursor: !isFile ? 'pointer' : undefined }} onClick={!isFile ? () => downloadFile(file.id) : undefined} component={'span'} variant="body2">
                  {!isFile ? `${file}.pdf` : file.name}
                </Typography>
              </Box>
              <Box textAlign={'end'}>
                <IconButton onClick={() => handleDelete(i)}>
                  <ClearIcon />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default StepPdf;
